import { Dialog } from "@headlessui/react";
import { PlusCircleIcon, XMarkIcon } from "@heroicons/react/24/outline";
import FormInputComponent from "@via/components/inputs/form-input-component";
import { Spinner } from "@via/components/loading/loading";
import SelectComponent from "@via/components/select/select";
import { useAuth } from "@via/hooks/useAuth";
import {
  useCreateVmetaConfigApi,
  useUpdateVmetaConfigApi,
  useVmetaCollections,
  useVmetaFieldsByCollectionIds,
} from "@via/pages/projects/metadata/metadata.api.tsx";
import ExtraGroupSection from "@via/pages/projects/metadata/configuration/api-configs/components/extra-group-section";
import MappingFieldsSection from "@via/pages/projects/metadata/configuration/api-configs/components/mapping-fields-section";
import {
  ApiConfigFormValues,
  ExtraGroupForm,
  defaultFieldMapping,
  defaultGroup,
  SYSTEM_FIELD_OPTIONS,
  toAutoPopulatedFieldMappings,
  toFormValues,
  toPayload,
} from "@via/pages/projects/metadata/configuration/api-configs/utils/helper";
import { USER_ROLES } from "@via/utils/constants";
import {
  requiredValidation,
  vmetaNameValidation,
} from "@via/utils/form-validators";
import { SelectOption } from "@via/utils/interfaces";
import {
  CreateVmetaConfigApiRequestBody,
  UpdateVmetaConfigApiRequestBody,
  VmetaConfigApi,
} from "common";
import { FC, useEffect, useMemo, useRef } from "react";
import { useFieldArray, useForm, useWatch } from "react-hook-form";

type ApiConfigFormMode = "create" | "edit" | "view";

interface ApiConfigFormProps {
  canvasId: string;
  mode: ApiConfigFormMode;
  initialConfig?: VmetaConfigApi | null;
  setOpen: (open: boolean) => void;
}

const ApiConfigForm: FC<ApiConfigFormProps> = ({
  canvasId,
  mode,
  initialConfig,
  setOpen,
}) => {
  const { user } = useAuth();
  const isReadOnly = mode === "view";
  const isAdmin = user?.userRole === USER_ROLES.ADMIN;

  const { mutateAsync: createConfigApi, isPending: isCreatePending } =
    useCreateVmetaConfigApi();
  const { mutateAsync: updateConfigApi, isPending: isUpdatePending } =
    useUpdateVmetaConfigApi();

  const { data: collectionsResponse, isLoading: isCollectionsLoading } =
    useVmetaCollections(
      0,
      1000,
      [],
      [
        {
          id: "projectID",
          value: canvasId,
        },
      ]
    );

  const {
    control,
    setValue,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<ApiConfigFormValues>({
    mode: "onSubmit",
    defaultValues: toFormValues(initialConfig),
  });

  useEffect(() => {
    const formValues = toFormValues(initialConfig);
    reset(formValues);
    previousTargetCollectionIdRef.current = formValues.collectionID;
    previousGroupCollectionIdsRef.current = formValues.groups.map(
      group => group.collectionID
    );
  }, [initialConfig, reset]);

  const {
    fields: groups,
    append: appendGroup,
    remove: removeGroup,
    update: updateGroup,
  } = useFieldArray({
    control,
    name: "groups",
  });
  const previousTargetCollectionIdRef = useRef<string>("");
  const previousGroupCollectionIdsRef = useRef<string[]>([]);

  const targetCollectionId = useWatch({
    control,
    name: "collectionID",
  }) as string;
  const watchedGroups = useWatch({
    control,
    name: "groups",
  }) as ExtraGroupForm[];

  const collectionOptions = useMemo<SelectOption[]>(() => {
    return (collectionsResponse?.data ?? []).map(collection => ({
      label: `${collection.label} (${collection.name})`,
      value: collection._id,
    }));
  }, [collectionsResponse?.data]);

  const fieldCollectionIds = useMemo(() => {
    const ids = new Set<string>();
    if (targetCollectionId) ids.add(targetCollectionId);
    (watchedGroups ?? []).forEach(group => {
      if (group?.collectionID) ids.add(group.collectionID);
      if (group?.enableJoin && group?.join?.source_collection) {
        ids.add(group.join.source_collection);
      }
    });
    return Array.from(ids);
  }, [targetCollectionId, watchedGroups]);

  const fieldQueries = useVmetaFieldsByCollectionIds(fieldCollectionIds);

  const fieldOptionsByCollectionId = fieldCollectionIds.reduce<
    Record<string, SelectOption[]>
  >((acc, collectionId, index) => {
    const response = fieldQueries[index]?.data;
    const fieldOptions = (response?.data ?? []).map(field => ({
      label: `${field.label} (${field.name})`,
      value: field.name,
    }));
    acc[collectionId] = [...SYSTEM_FIELD_OPTIONS, ...fieldOptions];
    return acc;
  }, {});

  const joinFieldOptionsByCollectionId = fieldCollectionIds.reduce<
    Record<string, SelectOption[]>
  >((acc, collectionId) => {
    const fieldOptions = fieldOptionsByCollectionId[collectionId] ?? [];
    acc[collectionId] = fieldOptions;
    return acc;
  }, {});

  useEffect(() => {
    if (isReadOnly) return;

    const previousTargetCollectionId = previousTargetCollectionIdRef.current;
    if (targetCollectionId === previousTargetCollectionId) return;

    if (!targetCollectionId) {
      setValue("targetFields", [defaultFieldMapping()], {
        shouldDirty: true,
      });
      previousTargetCollectionIdRef.current = "";
      return;
    }

    const fieldOptions = fieldOptionsByCollectionId[targetCollectionId];
    if (!fieldOptions) return;

    setValue("targetFields", toAutoPopulatedFieldMappings(fieldOptions), {
      shouldDirty: true,
    });
    previousTargetCollectionIdRef.current = targetCollectionId;
  }, [fieldOptionsByCollectionId, isReadOnly, setValue, targetCollectionId]);

  useEffect(() => {
    if (isReadOnly) return;

    const previousGroupCollectionIds = previousGroupCollectionIdsRef.current;
    const nextPreviousGroupCollectionIds = [...previousGroupCollectionIds];

    (watchedGroups ?? []).forEach((group, index) => {
      const collectionId = group?.collectionID ?? "";
      const previousCollectionId = previousGroupCollectionIds[index] ?? "";

      if (collectionId === previousCollectionId) {
        nextPreviousGroupCollectionIds[index] = previousCollectionId;
        return;
      }

      if (!collectionId) {
        updateGroup(index, {
          ...group,
          fields: [defaultFieldMapping()],
        });
        nextPreviousGroupCollectionIds[index] = "";
        return;
      }

      const fieldOptions = fieldOptionsByCollectionId[collectionId];
      if (!fieldOptions) {
        nextPreviousGroupCollectionIds[index] = previousCollectionId;
        return;
      }

      updateGroup(index, {
        ...group,
        fields: toAutoPopulatedFieldMappings(fieldOptions),
      });
      nextPreviousGroupCollectionIds[index] = collectionId;
    });

    nextPreviousGroupCollectionIds.length = watchedGroups?.length ?? 0;
    previousGroupCollectionIdsRef.current = nextPreviousGroupCollectionIds;
  }, [fieldOptionsByCollectionId, isReadOnly, updateGroup, watchedGroups]);

  const isSubmitting = isCreatePending || isUpdatePending;

  const handleSave = async (values: ApiConfigFormValues) => {
    const payload = toPayload(values);
    if (mode === "edit" && initialConfig?._id) {
      await updateConfigApi({
        canvasId,
        id: initialConfig._id,
        data: payload as UpdateVmetaConfigApiRequestBody,
      });
    } else {
      await createConfigApi({
        canvasId,
        data: payload as CreateVmetaConfigApiRequestBody,
      });
    }
    setOpen(false);
  };

  const title =
    mode === "create"
      ? "New API Config"
      : mode === "edit"
        ? "Edit API Config"
        : "API Config";

  return (
    <form
      className="flex h-full min-w-0 max-w-full flex-col divide-y divide-gray-200 overflow-hidden bg-white shadow-xl"
      onSubmit={e => {
        e.preventDefault();
        void handleSubmit(handleSave)(e);
      }}>
      <div className="border-b bg-gray-50 px-4 py-6 sm:px-6">
        <div className="flex items-center justify-between">
          <Dialog.Title className="text-base font-semibold leading-6 text-gray-900">
            {title}
          </Dialog.Title>
          <div className="ml-3 flex h-7 items-center">
            <button
              type="button"
              className="relative rounded-md text-gray-900 duration-150 ease-in-out hover:text-gray-600 focus:outline-none"
              onClick={() => setOpen(false)}>
              <span className="absolute -inset-2.5" />
              <span className="sr-only">Close panel</span>
              <XMarkIcon className="h-6 w-6" aria-hidden="true" />
            </button>
          </div>
        </div>
        <div className="mt-1">
          <p className="text-sm text-gray-500">
            Configure a custom `/format/:route` response for this sample
            tracker.
          </p>
        </div>
      </div>

      <div
        className="min-h-0 min-w-0 flex-1 overflow-y-scroll overscroll-contain"
        style={{ scrollbarGutter: "stable" }}>
        <div className="flex min-w-0 flex-col">
          <div className="min-w-0 divide-y divide-gray-200 px-2 sm:px-4">
            <div className="min-w-0 space-y-6 pb-5 pt-6">
              <FormInputComponent
                control={control}
                name="route"
                label="Route"
                placeholder="e.g. detailed"
                showRequiredField
                showTooltipIcon
                tooltipText="Used as the final token in /search/format/:route."
                validations={vmetaNameValidation("Route")}
                readOnly={isReadOnly}
              />

              <div>
                <label className="mb-2 flex items-center text-sm font-medium leading-6 text-gray-900">
                  Target Collection
                  <span className="text-red-400">&nbsp;*</span>
                </label>
                <SelectComponent
                  control={control}
                  name="collectionID"
                  options={collectionOptions}
                  placeholder="Select Collection"
                  enableClear={!isReadOnly}
                  readOnly={isReadOnly}
                  isSearchable
                  isLoading={isCollectionsLoading}
                  rules={requiredValidation("Target Collection")}
                />
                {errors.collectionID?.message && (
                  <span className="text-xs text-rose-500">
                    {String(errors.collectionID.message)}
                  </span>
                )}
              </div>

              <div className="min-w-0 space-y-4 rounded-lg border border-gray-200 p-4">
                <label className="text-sm font-medium leading-6 text-gray-900">
                  Target Collection Fields
                </label>
                <MappingFieldsSection
                  key={targetCollectionId || "target-fields-empty"}
                  control={control}
                  name="targetFields"
                  fieldOptions={
                    fieldOptionsByCollectionId[targetCollectionId] ?? []
                  }
                  readOnly={isReadOnly}
                />
              </div>

              <div className="min-w-0 space-y-4">
                <div className="flex flex-wrap items-center justify-between gap-3">
                  <label className="text-sm font-medium leading-6 text-gray-900">
                    Additional Collection Groups
                  </label>
                  {!isReadOnly && (
                    <button
                      type="button"
                      onClick={() => appendGroup(defaultGroup())}
                      className="inline-flex shrink-0 items-center gap-1 rounded-md bg-white px-3 py-2 text-sm font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50">
                      <PlusCircleIcon className="h-4 w-4" />
                      Insert Collection Group
                    </button>
                  )}
                </div>

                {groups.length === 0 ? (
                  <div className="rounded-md border border-dashed border-gray-300 p-4 text-sm text-gray-500">
                    No additional collection groups configured.
                  </div>
                ) : (
                  groups.map((group, index) => (
                    <ExtraGroupSection
                      key={group.id}
                      control={control}
                      index={index}
                      readOnly={isReadOnly}
                      onRemove={removeGroup}
                      collectionOptions={collectionOptions}
                      fieldOptionsByCollectionId={fieldOptionsByCollectionId}
                      joinFieldOptionsByCollectionId={
                        joinFieldOptionsByCollectionId
                      }
                    />
                  ))
                )}
              </div>

              {isReadOnly && isAdmin && initialConfig?.config && (
                <div className="rounded-lg border border-gray-200 p-4">
                  <label className="text-sm font-medium leading-6 text-gray-900">
                    Compiled Config Preview
                  </label>
                  <pre className="mt-3 max-h-80 overflow-auto rounded-md bg-gray-50 p-3 text-xs text-gray-700">
                    {JSON.stringify(initialConfig.config, null, 2)}
                  </pre>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      <div className="flex flex-shrink-0 justify-end px-4 py-4">
        <button
          type="button"
          className="rounded-md bg-white px-3 py-2 text-sm font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50"
          onClick={() => setOpen(false)}>
          {isReadOnly ? "Close" : "Cancel"}
        </button>
        {!isReadOnly && (
          <button
            type="submit"
            disabled={isSubmitting}
            className="ml-4 inline-flex justify-center rounded-md bg-indigo-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-indigo-500 disabled:opacity-70">
            <Spinner loading={isSubmitting}>Save</Spinner>
          </button>
        )}
      </div>
    </form>
  );
};

export default ApiConfigForm;
